import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../../api/ApiEndpoints';
import {
  LicensesActivityResponse,
  LicensesAssignmentResponse,
  LicensesTrendResponse,
  LicensesOverassignedResponse,
  UseLicensesActivityQueryOptions,
  UseLicensesAssignmentQueryOptions,
  UseLicensesTrendQueryOptions,
  UseLicensesOverassignedQueryOptions,
  LicensesAssignmentPreviewResponse,
  UseLicensesAssignmentPreviewQueryOptions,
  LicensesFiltersResponse,
  UseLicensesUsageTrendQueryOptions,
  LicensesUsageTrendResponse,
  UseLicensesCallTimeQueryOptions,
  LicensesCallTimeResponse,
  UseLicensesCorrespondencedQueryOptions,
  LicensesCorrespondenceResponse,
  UseLicensesUsagePreviewQueryOptions,
  LicensesUsagePreviewResponse,
  LicensesExpiringResponse,
  UseLicensesExpiringPreviewQueryOptions,
  LicensesExpiringVendorResponse,
  UseLicensesExpiringVendorQueryOptions,
  LicensesExpiringUserResponse,
  LicensesExpiredResponse,
  LicencesExpiredBarChartResponse,
  RedundantLicensesFiltersResponse,
  LicensesInactiveResponse,
  LicensesRedundantResponse,
  RedundantLicenseFilterResponse,
} from './types';
import { useHttpClient } from '@hooks/utils/httpConfig';
import { getSearchQueryParams } from '@utils/index';

export function useLicencesActivityQuery({
  days,
  includeFreeLicenses,
  enabled,
}: UseLicensesActivityQueryOptions = {}): UseQueryResult<LicensesActivityResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesActivity = async (): Promise<LicensesActivityResponse> => {
    const params = {
      days,
      includeFreeLicenses: includeFreeLicenses
        ? `${includeFreeLicenses}`
        : 'false',
    };
    return licensesClient.get<LicensesActivityResponse>(
      API_ENDPOINTS.LICENSES_ACTIVITY,
      params
    );
  };

  return useQuery({ queryKey: ['licensesActivity', days, includeFreeLicenses], queryFn: fetchLicensesActivity, enabled });
}

export function useLicencesExpiringSnapshotQuery({
  vendorList,
  autoRenew,
  renewalPeriod,
  subscriptionNameList,
  includeFreeLicenses,
  enabled,
}: UseLicensesAssignmentQueryOptions = {}): UseQueryResult<LicensesExpiringResponse> {
  const licensesClient = useHttpClient();
  const fetchLicencesExpiring = async (): Promise<LicensesExpiringResponse> => {
    const baseParams: Record<string, string> = {};

    if (autoRenew) baseParams.autoRenew = autoRenew;
    if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;

    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const subscriptionNameListParams = subscriptionNameList
      ? subscriptionNameList
          .map((licenseType) => `SubscriptionNameList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_EXPIRING_SNAPSHOT
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

    return licensesClient.get<LicensesExpiringResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licencesExpiring',
      vendorList,
      autoRenew,
      renewalPeriod,
      subscriptionNameList,
      includeFreeLicenses,
    ], queryFn: fetchLicencesExpiring, enabled });
}

export function useLicencesExpiredSnapshotBarChartQuery({
  vendorList,
  autoRenew,
  renewalPeriod,
  subscriptionNameList,
  includeFreeLicenses,
  enabled,
}: UseLicensesAssignmentQueryOptions = {}): UseQueryResult<LicencesExpiredBarChartResponse> {
  const licensesClient = useHttpClient();
  const fetchLicencesExpiredBarChart =
    async (): Promise<LicencesExpiredBarChartResponse> => {
      const baseParams: Record<string, string> = {};

      if (autoRenew) baseParams.autoRenew = autoRenew;
      if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;

      if (includeFreeLicenses) {
        baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
      }

      const vendorListParams = vendorList
        ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
        : '';
      const subscriptionNameListParams = subscriptionNameList
        ? subscriptionNameList
            .map((licenseType) => `SubscriptionNameList=${licenseType}`)
            .join('&')
        : '';

      const endpoint = `${
        API_ENDPOINTS.LICENSES_EXPIRED_SNAPSHOT_BAR_CHART
      }?${new URLSearchParams(
        baseParams
      ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

      return licensesClient.get<LicencesExpiredBarChartResponse>(endpoint);
    };

  return useQuery({ queryKey: [
      'licencesExpiredBarChart',
      vendorList,
      autoRenew,
      renewalPeriod,
      subscriptionNameList,
      includeFreeLicenses,
    ], queryFn: fetchLicencesExpiredBarChart, enabled });
}

export function useLicencesExpiredSnapshotQuery({
  vendorList,
  autoRenew,
  renewalPeriod,
  subscriptionNameList,
  includeFreeLicenses,
  enabled,
}: UseLicensesAssignmentQueryOptions = {}): UseQueryResult<LicensesExpiredResponse> {
  const licensesClient = useHttpClient();
  const fetchLicencesExpired = async (): Promise<LicensesExpiredResponse> => {
    const baseParams: Record<string, string> = {};

    if (autoRenew) baseParams.autoRenew = autoRenew;
    if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;
    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const subscriptionNameListParams = subscriptionNameList
      ? subscriptionNameList
          .map((licenseType) => `SubscriptionNameList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_EXPIRED_SNAPSHOT
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

    return licensesClient.get<LicensesExpiredResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licencesExpired',
      vendorList,
      autoRenew,
      renewalPeriod,
      subscriptionNameList,
      includeFreeLicenses,
    ], queryFn: fetchLicencesExpired, enabled });
}

export function useLicencesExpiringSnapshotVendorQuery({
  vendorList,
  autoRenew,
  renewalPeriod,
  subscriptionNameList,
  includeFreeLicenses,
  enabled,
}: UseLicensesExpiringVendorQueryOptions = {}): UseQueryResult<LicensesExpiringVendorResponse> {
  const licensesClient = useHttpClient();
  const fetchLicencesExpiring =
    async (): Promise<LicensesExpiringVendorResponse> => {
      const baseParams: Record<string, string> = {};

      if (autoRenew) baseParams.autoRenew = autoRenew;
      if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;

      if (includeFreeLicenses) {
        baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
      }

      const vendorListParams = vendorList
        ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
        : '';
      const subscriptionNameListParams = subscriptionNameList
        ? subscriptionNameList
            .map((licenseType) => `SubscriptionNameList=${licenseType}`)
            .join('&')
        : '';

      const endpoint = `${
        API_ENDPOINTS.LICENSES_EXPIRING_SNAPSHOT_VENDOR
      }?${new URLSearchParams(
        baseParams
      ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

      return licensesClient.get<LicensesExpiringVendorResponse>(endpoint);
    };

  return useQuery({ queryKey: [
      'licencesExpiringVendor',
      vendorList,
      autoRenew,
      renewalPeriod,
      subscriptionNameList,
      includeFreeLicenses,
    ], queryFn: fetchLicencesExpiring, enabled });
}

export function useLicencesExpiringSnapshotUserQuery({
  days,
  vendorList,
  autoRenew,
  renewalPeriod,
  subscriptionNameList,
  includeFreeLicenses,
}: UseLicensesExpiringVendorQueryOptions = {}): UseQueryResult<LicensesExpiringUserResponse> {
  const licensesClient = useHttpClient();
  const fetchLicencesExpiring =
    async (): Promise<LicensesExpiringUserResponse> => {
      const baseParams: Record<string, string> = {
        days: days || '7',
      };

      if (autoRenew) baseParams.autoRenew = autoRenew;
      if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;

      if (includeFreeLicenses) {
        baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
      }

      const vendorListParams = vendorList
        ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
        : '';
      const subscriptionNameListParams = subscriptionNameList
        ? subscriptionNameList
            .map((licenseType) => `SubscriptionNameList=${licenseType}`)
            .join('&')
        : '';

      const endpoint = `${
        API_ENDPOINTS.LICENSES_EXPIRING_USER
      }?${new URLSearchParams(
        baseParams
      ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

      return licensesClient.get<LicensesExpiringUserResponse>(endpoint);
    };

  return useQuery({ queryKey: [
      'licencesExpiringUser',
      days,
      vendorList,
      autoRenew,
      renewalPeriod,
      subscriptionNameList,
      includeFreeLicenses,
    ], queryFn: fetchLicencesExpiring });
}

export function useLicencesAssignmentQuery({
  vendorList,
  includeFreeLicenses,
  enabled,
}: UseLicensesAssignmentQueryOptions = {}): UseQueryResult<LicensesAssignmentResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesAssignment =
    async (): Promise<LicensesAssignmentResponse> => {
      const params = new URLSearchParams();
      params.append('Days', '1');

      params.append(
        'includeFreeLicenses',
        includeFreeLicenses ? `${includeFreeLicenses}` : 'false'
      );

      if (vendorList) {
        vendorList.forEach((vendor) => {
          params.append('vendorList', vendor);
        });
      }
      return licensesClient.get<LicensesAssignmentResponse>(
        `${API_ENDPOINTS.LICENSES_ASSIGNMENT}?${params.toString()}`
      );
    };

  return useQuery({ queryKey: ['licensesAssignment', vendorList, includeFreeLicenses], queryFn: fetchLicensesAssignment, enabled });
}

export function useLicencesFiltersQuery(
  includeEnabledOnly?: string,
  includeFreeLicenses?: boolean,
  enabled?: boolean
): UseQueryResult<LicensesFiltersResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesFilters = async (): Promise<LicensesFiltersResponse> => {
    const params = new URLSearchParams();

    params.append(
      'includeEnabledOnly',
      includeEnabledOnly ? `${includeEnabledOnly}` : 'true'
    );

    params.append(
      'includeFreeLicenses',
      includeFreeLicenses ? `${includeFreeLicenses}` : 'false'
    );
    return licensesClient.get<LicensesFiltersResponse>(
      `${API_ENDPOINTS.LICENSES_FILTERS}?${params.toString()}`
    );
  };

  return useQuery({ queryKey: ['licensesFilters', includeEnabledOnly, includeFreeLicenses], queryFn: fetchLicensesFilters, enabled });
}

export function useRedundantLicencesFiltersQuery(): UseQueryResult<RedundantLicensesFiltersResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesFilters =
    async (): Promise<RedundantLicensesFiltersResponse> => {
      return licensesClient.get<RedundantLicensesFiltersResponse>(
        `${API_ENDPOINTS.LICENSES_REDUNDANT_SNAPSHOT_FILTER}`
      );
    };

  return useQuery({ queryKey: ['redundantLicensesFilters'], queryFn: fetchLicensesFilters });
}

export function useRedundantDataLicencesFiltersQuery(
): UseQueryResult<RedundantLicenseFilterResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesFilters = async (): Promise<RedundantLicenseFilterResponse> => {
    return licensesClient.get<RedundantLicenseFilterResponse>(
      `${API_ENDPOINTS.OVERASSIGNED_LICENSES_FILTERS}`
    );
  };

  return useQuery({ queryKey: ['licensesFilters'], queryFn: fetchLicensesFilters });
}


export function useRedundantSnapshotLicencesFiltersQuery(
  enabled?: boolean
): UseQueryResult<RedundantLicensesFiltersResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesFilters =
    async (): Promise<RedundantLicensesFiltersResponse> => {
      return licensesClient.get<RedundantLicensesFiltersResponse>(
        `${API_ENDPOINTS.LICENSES_REDUNDANT_SNAPSHOT_FILTER}`
      );
    };

  return useQuery({ queryKey: ['redundantSnapshotLicensesFilters'], queryFn: fetchLicensesFilters, enabled });
}

export function useLicencesOverassignedQuery({
  vendorList,
  groupList,
  locationList,
  reasonList,
  licenseTypesList,
}: UseLicensesOverassignedQueryOptions = {}): UseQueryResult<LicensesOverassignedResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesOverassigned =
    async (): Promise<LicensesOverassignedResponse> => {
      const params = new URLSearchParams();
      params.append('Days', '30');

      if (vendorList) {
        vendorList.forEach((vendor) => {
          params.append('VendorList', vendor);
        });
      }
      if (licenseTypesList) {
        licenseTypesList.forEach((type) => {
          params.append('LicenseTypesList', type);
        });
      }
      if (reasonList) {
        reasonList.forEach((vendor) => {
          params.append('ReasonList', vendor);
        });
      }
      if (groupList) {
        groupList.forEach((group) => {
          params.append('GroupList', group);
        });
      }
      if (locationList) {
        locationList.forEach((location) => {
          params.append('LocationList', location);
        });
      }
      return licensesClient.get<LicensesOverassignedResponse>(
        `${API_ENDPOINTS.LICENSES_OVERASSIGNED_SNAPSHOT}?${params.toString()}`
      );
    };

  return useQuery({ queryKey: [
      'licensesOverassigned',
      vendorList,
      reasonList,
      licenseTypesList,
      groupList,
      locationList,
    ], queryFn: fetchLicensesOverassigned });
}

export function useLicencesInactiveQuery({
  vendorList,
  groupList,
  locationList,
  reasonList,
  licenseTypesList,
}: UseLicensesOverassignedQueryOptions = {}): UseQueryResult<LicensesInactiveResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesInactive = async (): Promise<LicensesInactiveResponse> => {
    const params = new URLSearchParams();
    params.append('Days', '30');

    if (vendorList) {
      vendorList.forEach((vendor) => {
        params.append('VendorList', vendor);
      });
    }
    if (licenseTypesList) {
      licenseTypesList.forEach((type) => {
        params.append('LicenseTypeList', type);
      });
    }
    console.log('licenseTypesList',licenseTypesList )

    if (reasonList) {
      reasonList.forEach((vendor) => {
        params.append('ReasonList', vendor);
      });
    }
    if (groupList) {
      groupList.forEach((group) => {
        params.append('GroupList', group);
      });
    }
    if (locationList) {
      locationList.forEach((location) => {
        params.append('LocationList', location);
      });
    }
    return licensesClient.get<LicensesInactiveResponse>(
      `${API_ENDPOINTS.LICENSES_INACTIVE_SNAPSHOT}?${params.toString()}`
    );
  };

  return useQuery({ queryKey: [
      'licensesInactive',
      vendorList,
      reasonList,
      licenseTypesList,
      groupList,
      locationList,
    ], queryFn: fetchLicensesInactive });
}

export function useLicencesRedundantQuery({
  vendorList,
  reasonList,
  enabled,
}: UseLicensesOverassignedQueryOptions = {}): UseQueryResult<LicensesRedundantResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesRedundant =
    async (): Promise<LicensesRedundantResponse> => {
      const params = new URLSearchParams();
      params.append('Days', '30');

      if (vendorList) {
        vendorList.forEach((vendor) => {
          params.append('VendorList', vendor);
        });
      }
      if (reasonList) {
        reasonList.forEach((vendor) => {
          params.append('ReasonList', vendor);
        });
      }
      return licensesClient.get<LicensesRedundantResponse>(
        `${API_ENDPOINTS.LICENSES_REDUNDANT_SNAPSHOT}?${params.toString()}`
      );
    };

  return useQuery({ queryKey: [
      'licensesRedundant',
      vendorList,
      reasonList,
    ], queryFn: fetchLicensesRedundant, enabled });
}


export function useLicensesAssignmentPreviewQuery({
  searchTerm,
  sortBy,
  order,
  pageSize = 20,
  pageNumber = 1,
  vendorList,
  licenseTypeList,
  includeFreeLicenses,
}: UseLicensesAssignmentPreviewQueryOptions = {}): UseQueryResult<LicensesAssignmentPreviewResponse> {
  const licensesClient = useHttpClient();

  const fetchData = async (): Promise<LicensesAssignmentPreviewResponse> => {
    const params = getSearchQueryParams({
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      includeFreeLicenses: includeFreeLicenses?.toString(),
      VendorList: vendorList,
      LicenseTypeList: licenseTypeList,
    });

    const endpoint = `${
      API_ENDPOINTS.LICENSES_ASSIGNMENT_PREVIEW
    }?${params.toString()}`;
    return licensesClient.get<LicensesAssignmentPreviewResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesAssignmentPreview',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      vendorList,
      licenseTypeList,
      includeFreeLicenses,
    ], queryFn: fetchData });
}

export function useLicencesTrendQuery({
  days,
  vendorList,
  licenseTypeList,
  includeFreeLicenses,
}: UseLicensesTrendQueryOptions = {}): UseQueryResult<LicensesTrendResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesUtilisation = async (): Promise<LicensesTrendResponse> => {
    const baseParams: Record<string, string> = {};

    if (days) baseParams.days = days;

    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${API_ENDPOINTS.LICENSES_TREND}?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}`;

    return licensesClient.get<LicensesTrendResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesTrend',
      { days, vendorList, licenseTypeList, includeFreeLicenses },
    ], queryFn: fetchLicensesUtilisation });
}

export function useLicencesActivityTrendQuery({
  days,
  vendorList,
  licenseTypeList,
  groupList,
  locationList,
  includeFreeLicenses,
}: UseLicensesTrendQueryOptions = {}): UseQueryResult<LicensesTrendResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesUtilisation = async (): Promise<LicensesTrendResponse> => {
    const baseParams: Record<string, string> = {};

    if (days) baseParams.days = days;

    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    const groupListParams = groupList
      ? groupList.map((group) => `GroupList=${group}`).join('&')
      : '';

    const locationListParams = locationList
      ? locationList.map((location) => `LocationList=${location}`).join('&')
      : '';

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_ACTIVITY_TREND
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}&${groupListParams}&${locationListParams}`;

    return licensesClient.get<LicensesTrendResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesActivityTrend',
      {
        days,
        vendorList,
        licenseTypeList,
        groupList,
        locationList,
        includeFreeLicenses,
      },
    ], queryFn: fetchLicensesUtilisation });
}

export function useLicencesOverassignedTrendQuery({
  days,
  vendorList,
  licenseTypeList,
  reasonList,
  groupList,
  locationList,
}: UseLicensesTrendQueryOptions = {}): UseQueryResult<LicensesTrendResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesTrend = async (): Promise<LicensesTrendResponse> => {
    const baseParams: Record<string, string> = {};

    if (days) baseParams.days = days;
    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const groupListParams = groupList
      ? groupList.map((vendor) => `GroupList=${vendor}`).join('&')
      : '';
    const locationListParams = locationList
      ? locationList.map((vendor) => `LocationList=${vendor}`).join('&')
      : '';
    const reasonListParams = reasonList
      ? reasonList.map((vendor) => `ReasonList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';
    const endpoint = `${
      API_ENDPOINTS.LICENSES_OVERASSIGNED_TREND
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}&${groupListParams}&${locationListParams}&${reasonListParams}`;
    return licensesClient.get<LicensesTrendResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesOverassignedTrend',
      days,
      vendorList,
      licenseTypeList,
      reasonList,
      groupList,
      locationList,
    ], queryFn: fetchLicensesTrend });
}

export function useLicensesActivityPreviewQuery({
  searchTerm,
  sortBy,
  order,
  pageSize = 20,
  pageNumber = 1,
  vendorList,
  licenseTypeList,
  includeFreeLicenses,
}: UseLicensesAssignmentPreviewQueryOptions = {}): UseQueryResult<LicensesAssignmentPreviewResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesAssignmentPreview =
    async (): Promise<LicensesAssignmentPreviewResponse> => {
      const baseParams: Record<string, string> = {
        pageSize: pageSize.toString(),
        pageNumber: pageNumber.toString(),
      };

      if (searchTerm) baseParams.searchTerm = searchTerm;
      if (sortBy) baseParams.sortBy = sortBy;
      if (order) baseParams.order = order;

      if (includeFreeLicenses) {
        baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
      }

      const vendorListParams = vendorList
        ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
        : '';
      const licenseTypeListParams = licenseTypeList
        ? licenseTypeList
            .map((licenseType) => `LicenseTypeList=${licenseType}`)
            .join('&')
        : '';

      const endpoint = `${
        API_ENDPOINTS.LICENSES_ACTIVITY_PREVIEW
      }?${new URLSearchParams(
        baseParams
      ).toString()}&${vendorListParams}&${licenseTypeListParams}`;

      return licensesClient.get<LicensesAssignmentPreviewResponse>(endpoint);
    };

  return useQuery({ queryKey: [
      'licensesActivityPreview',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      vendorList,
      licenseTypeList,
      includeFreeLicenses,
    ], queryFn: fetchLicensesAssignmentPreview });
}

export function useLicensesOverassignedPreviewQuery({
  searchTerm,
  sortBy,
  order,
  pageSize = 20,
  pageNumber = 1,
  vendorList,
  licenseTypeList,
  locationList,
  groupList,
  reasonList,
}: UseLicensesAssignmentPreviewQueryOptions = {}): UseQueryResult<LicensesAssignmentPreviewResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesAssignmentPreview =
    async (): Promise<LicensesAssignmentPreviewResponse> => {
      const params = new URLSearchParams();
      params.append('Days', '1');
      params.append('pageSize', pageSize.toString());
      params.append('pageNumber', pageNumber.toString());
      if (searchTerm) params.append('searchTerm', searchTerm);
      if (sortBy) params.append('sortBy', sortBy);
      if (order) params.append('order', order);
      if (vendorList) {
        vendorList.forEach((vendor) => {
          params.append('VendorList', vendor);
        });
      }
      if (reasonList) {
        reasonList.forEach((reason) => {
          params.append('ReasonList', reason);
        });
      }
      if (groupList) {
        groupList.forEach((vendor) => {
          params.append('GroupList', vendor);
        });
      }
      if (locationList) {
        locationList.forEach((vendor) => {
          params.append('LocationList', vendor);
        });
      }
      if (licenseTypeList) {
        licenseTypeList.forEach((vendor) => {
          params.append('LicenseTypeList', vendor);
        });
      }

      return licensesClient.get<LicensesAssignmentPreviewResponse>(
        `${API_ENDPOINTS.LICENSES_OVERASSIGNED}?${params.toString()}`
      );
    };

  return useQuery({ queryKey: [
      'licensesOverassignedPreview',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      vendorList,
      licenseTypeList,
      locationList,
      groupList,
      reasonList,
    ], queryFn: fetchLicensesAssignmentPreview });
}

export function useLicensesExpiringPreviewQuery({
  searchTerm,
  sortBy,
  order,
  pageSize = 20,
  pageNumber = 1,
  vendorList,
  subscriptionNameList,
  autoRenew,
  renewalPeriod,
  includeFreeLicenses,
}: UseLicensesExpiringPreviewQueryOptions = {}): UseQueryResult<LicensesAssignmentPreviewResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesExpiringPreview =
    async (): Promise<LicensesAssignmentPreviewResponse> => {
      const baseParams: Record<string, string> = {
        pageSize: pageSize.toString(),
        pageNumber: pageNumber.toString(),
      };

      if (searchTerm) baseParams.searchTerm = searchTerm;
      if (sortBy) baseParams.sortBy = sortBy;
      if (order) baseParams.order = order;
      if (autoRenew) baseParams.autoRenew = autoRenew;
      if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;
      if (includeFreeLicenses) {
        baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
      }

      const vendorListParams = vendorList
        ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
        : '';
      const subscriptionNameListParams = subscriptionNameList
        ? subscriptionNameList
            .map((licenseType) => `SubscriptionNameList=${licenseType}`)
            .join('&')
        : '';

      const endpoint = `${
        API_ENDPOINTS.LICENSES_EXPIRING_PREVIEW
      }?${new URLSearchParams(
        baseParams
      ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

      return licensesClient.get<LicensesAssignmentPreviewResponse>(endpoint);
    };

  return useQuery({ queryKey: [
      'licensesExpiringPreview',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      vendorList,
      subscriptionNameList,
      autoRenew,
      renewalPeriod,
      includeFreeLicenses,
    ], queryFn: fetchLicensesExpiringPreview });
}

export function useLicensesExpiredPreviewQuery({
  searchTerm,
  sortBy,
  order,
  pageSize = 20,
  pageNumber = 1,
  vendorList,
  subscriptionNameList,
  autoRenew,
  renewalPeriod,
  includeFreeLicenses,
}: UseLicensesExpiringPreviewQueryOptions = {}): UseQueryResult<LicensesAssignmentPreviewResponse> {
  const licensesClient = useHttpClient();
  const fetchLicensesExpiredPreview =
    async (): Promise<LicensesAssignmentPreviewResponse> => {
      const baseParams: Record<string, string> = {
        pageSize: pageSize.toString(),
        pageNumber: pageNumber.toString(),
      };

      if (searchTerm) baseParams.searchTerm = searchTerm;
      if (sortBy) baseParams.sortBy = sortBy;
      if (order) baseParams.order = order;
      if (autoRenew) baseParams.autoRenew = autoRenew;
      if (renewalPeriod) baseParams.renewalPeriod = renewalPeriod;

      if (includeFreeLicenses) {
        baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
      }

      const vendorListParams = vendorList
        ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
        : '';
      const subscriptionNameListParams = subscriptionNameList
        ? subscriptionNameList
            .map((licenseType) => `SubscriptionNameList=${licenseType}`)
            .join('&')
        : '';

      const endpoint = `${
        API_ENDPOINTS.LICENSES_EXPIRED_PREVIEW
      }?${new URLSearchParams(
        baseParams
      ).toString()}&${vendorListParams}&${subscriptionNameListParams}`;

      return licensesClient.get<LicensesAssignmentPreviewResponse>(endpoint);
    };

  return useQuery({ queryKey: [
      'licensesExpiredPreview',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      vendorList,
      subscriptionNameList,
      autoRenew,
      renewalPeriod,
      includeFreeLicenses,
    ], queryFn: fetchLicensesExpiredPreview });
}

export function useLicencesUsageTrendQuery({
  days,
  serviceType,
  vendorList,
  licenseTypeList,
  locationList,
  groupList,
  includeFreeLicenses,
  enabled,
  licensedOnly,
}: UseLicensesUsageTrendQueryOptions = {}): UseQueryResult<LicensesUsageTrendResponse> {
  const licensesClient = useHttpClient();
  const fetchData = async (): Promise<LicensesUsageTrendResponse> => {
    const baseParams: Record<string, string> = {};

    if (days) baseParams.Days = days;
    if (serviceType) baseParams.ServiceType = serviceType;

    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    if (licensedOnly !== undefined) {
      baseParams.LicensedOnly = `${licensedOnly}`;
    }

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_USAGE_TREND
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}`;

    return licensesClient.get<LicensesUsageTrendResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesUsageTrend',
      {
        days,
        serviceType,
        vendorList,
        licenseTypeList,
        locationList,
        groupList,
        includeFreeLicenses,
        licensedOnly,
      },
    ], queryFn: fetchData, enabled });
}

export function useLicencesUsageCallTimeQuery({
  days,
  vendorList,
  licenseTypeList,
  locationList,
  groupList,
  includeFreeLicenses,
  licensedOnly,
}: UseLicensesCallTimeQueryOptions = {}): UseQueryResult<LicensesCallTimeResponse> {
  const licensesClient = useHttpClient();
  const fetchData = async (): Promise<LicensesCallTimeResponse> => {
    const baseParams: Record<string, string> = {};
    if (days) baseParams.Days = days;

    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    if (licensedOnly !== undefined) {
      baseParams.LicensedOnly = `${licensedOnly}`;
    }

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_USAGE_CALL_TIME
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}`;

    return licensesClient.get<LicensesCallTimeResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesUsageCallTime',
      {
        days,
        vendorList,
        licenseTypeList,
        locationList,
        groupList,
        includeFreeLicenses,
        licensedOnly,
      },
    ], queryFn: fetchData });
}

export function useLicencesUsageCorrespondenceQuery({
  days,
  serviceType,
  vendorList,
  licenseTypeList,
  locationList,
  groupList,
}: UseLicensesCorrespondencedQueryOptions = {}): UseQueryResult<LicensesCorrespondenceResponse> {
  const licensesClient = useHttpClient();
  const fetchData = async (): Promise<LicensesCorrespondenceResponse> => {
    const baseParams: Record<string, string> = {};
    if (days) baseParams.Days = days;
    if (serviceType) baseParams.ServiceType = serviceType;

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_USAGE_CORRESPODENCE
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}`;

    return licensesClient.get<LicensesCorrespondenceResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesUsageCorrespodence',
      {
        days,
        serviceType,
        vendorList,
        licenseTypeList,
        locationList,
        groupList,
      },
    ], queryFn: fetchData });
}

export function useLicensesUsagePreviewQuery({
  searchTerm,
  sortBy,
  order,
  pageSize = 20,
  pageNumber = 1,
  vendorList,
  licenseTypeList,
  locationList,
  groupList,
  includeFreeLicenses,
  licensedOnly,
}: UseLicensesUsagePreviewQueryOptions = {}): UseQueryResult<LicensesUsagePreviewResponse> {
  const licensesClient = useHttpClient();
  const fetchData = async (): Promise<LicensesUsagePreviewResponse> => {
    const baseParams: Record<string, string> = {
      pageSize: pageSize.toString(),
      pageNumber: pageNumber.toString(),
    };

    if (searchTerm) baseParams.searchTerm = searchTerm;
    if (sortBy) baseParams.sortBy = sortBy;
    if (order) baseParams.order = order;

    if (includeFreeLicenses) {
      baseParams.includeFreeLicenses = `${includeFreeLicenses}`;
    }

    if (licensedOnly !== undefined) {
      baseParams.LicensedOnly = `${licensedOnly}`;
    }

    const vendorListParams = vendorList
      ? vendorList.map((vendor) => `VendorList=${vendor}`).join('&')
      : '';
    const licenseTypeListParams = licenseTypeList
      ? licenseTypeList
          .map((licenseType) => `LicenseTypeList=${licenseType}`)
          .join('&')
      : '';
    const locationListParams = locationList
      ? locationList.map((location) => `LocationList=${location}`).join('&')
      : '';
    const groupListParams = groupList
      ? groupList.map((group) => `GroupList=${group}`).join('&')
      : '';

    const endpoint = `${
      API_ENDPOINTS.LICENSES_USAGE_PREVIEW
    }?${new URLSearchParams(
      baseParams
    ).toString()}&${vendorListParams}&${licenseTypeListParams}&${locationListParams}&${groupListParams}`;

    return licensesClient.get<LicensesUsagePreviewResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'licensesUsagePreview',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      vendorList,
      licenseTypeList,
      locationList,
      groupList,
      includeFreeLicenses,
      licensedOnly,
    ], queryFn: fetchData });
}
